import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { type UUID } from '~/types/common';

import {
  type DeliveryNoteOption,
  getDeliveryNoteOptionsQueryOptions,
} from '~/data/deliveryNote';

import { type Option, SelectServerDriven } from './SelectServerDriven';

const MIN_QUERY_LENGTH = 2;

const getOptionLabel = ({ dlnNumber }: DeliveryNoteOption) => dlnNumber;

const alwaysAllowed = () => true;

type P = {
  readonly isDisabled?: boolean;
  readonly onChange: (assetIds: UUID[]) => void;
  readonly placeholder?: string;
  readonly value: UUID[];
} & ComponentStyling;

/**
 * Multi-select delivery-note picker with async fulltext search (by DLN number).
 * The backend's search column does not cover asset ids, so the data layer
 * resolves a pasted UUID via single-DLN lookup instead — the user still picks
 * a verified hit with a real DLN-number label.
 */
export const MultiSelectDeliveryNotes = ({
  className,
  isDisabled = false,
  onChange,
  placeholder,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();

  // Options already surfaced to the user, so selected values keep their DLN-number
  // label when the search results (and their short-lived query cache) move on.
  const knownOptionsRef = useRef<Map<UUID, DeliveryNoteOption> | null>(null);
  knownOptionsRef.current ??= new Map();
  const knownOptions = knownOptionsRef.current;

  const loadOptions = async (searchString: string) => {
    const query = searchString.trim();

    if (query.length < MIN_QUERY_LENGTH) {
      return { hasMore: false, options: [] };
    }

    const results = await queryClient.fetchQuery(
      getDeliveryNoteOptionsQueryOptions({ query }),
    );

    for (const option of results) {
      knownOptions.set(option.assetId, option);
    }

    return {
      hasMore: false,
      options: results.map((option): Option<DeliveryNoteOption> => ({
        data: option,
        label: option.dlnNumber,
        value: option.assetId,
      })),
    };
  };

  // There is no single-DLN options endpoint; every selectable option passed through
  // loadOptions, so resolve from there and fall back to the raw id.
  const getItemData = async (
    assetId: string,
  ): Promise<DeliveryNoteOption | undefined> => {
    if (!assetId) {
      return undefined;
    }

    return knownOptions.get(assetId) ?? { assetId, dlnNumber: assetId };
  };

  return (
    <SelectServerDriven<DeliveryNoteOption>
      className={className}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isDisabled={isDisabled}
      isItemSelectionAllowed={alwaysAllowed}
      isMultiSelect
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={(assetIds) => {
        onChange(Array.isArray(assetIds) ? assetIds : [assetIds]);
      }}
    />
  );
};
